import { useState } from 'react';
import { useSnackbar } from '../contexts/SnackbarProvider';
import axiosInstance from '../utils/axiosInstance';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Stack, Snackbar, Alert,
    Button, TextField, MenuItem, Typography, Tooltip, IconButton
} from '@mui/material';
import { HelpOutline } from '@mui/icons-material';

const CategoryAdd = ({ open, onClose, categories = [], loadCategories, setSelectedCategory }) => {
    const { showSnackbar } = useSnackbar();
    const [name, setName] = useState('');
    const [parent, setParent] = useState(null);
    const [saving, setSaving] = useState(false);
    const reset = () => {
        setName('');
        setParent(null);
    };

    const handleSubmit = async () => {
        const trimmedName = name.trim();
        if (!trimmedName) {
            showSnackbar('カテゴリ名を入力してください', 'warning');
            return;
        }

        if (trimmedName.length > 255) {
            showSnackbar('カテゴリ名は255文字以内で入力してください', 'error');
            return;
        }

        setSaving(true);

        try {
            const res = await axiosInstance.post('/product/category-add', {
                category_name: trimmedName,
                parent_category_id: parent,
            });

            if (loadCategories) await loadCategories();

            if (setSelectedCategory && res.data?.category_id) {
                setSelectedCategory(res.data.category_id);
            }
            showSnackbar(res?.data?.message || 'カテゴリを追加しました', 'success');
            reset();
            onClose();

        } catch (err) {
            if (process.env.NODE_ENV === 'development') {
                console.error(err);
            }
            showSnackbar(err?.response?.data?.message || 'システムエラーが発生しました', 'error');
        } finally {
            setSaving(false);
        }
    };
    const handleClose = () => {
        if (saving) return;
        reset();
        onClose();
    };
    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>
                <Stack direction='row' alignItems='center'>
                    カテゴリ追加
                    <Tooltip
                        title={
                            <Typography variant="body2">
                                「設定＞商品カテゴリ管理」を利用すると、すべてのカテゴリの追加、変更、削除が可能です。
                            </Typography>
                        }
                        arrow
                        placement="right"
                    >
                        <IconButton size="small" aria-label="help">
                            <HelpOutline fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </DialogTitle>
            <DialogContent >
                <Stack spacing={2} mt={3}>
                    <TextField
                        label="カテゴリ名"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        error={name.length > 255}
                        helperText={
                            name.length > 255
                                ? 'カテゴリ名は255文字以内で入力してください'
                                : `${name.length}/255`
                        }
                        inputProps={{ maxLength: 255 }}
                    />

                    <TextField
                        select
                        label="親カテゴリ（任意）"
                        value={parent ?? ''}
                        onChange={(e) => setParent(e.target.value ? Number(e.target.value) : null)}
                    >
                        <MenuItem value="">（親なし）</MenuItem>
                        {categories.map((c) => (
                            <MenuItem
                                key={c.category_id}
                                value={c.category_id}
                                sx={{ pl: (c.level ?? 0) * 2 }} // levelに応じてインデント
                            >
                                {c.category_name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={saving} variant="outlined">
                    キャンセル
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!name.trim() || saving || name.length > 255}
                >
                    {saving ? '保存中…' : '保存'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CategoryAdd;
